import { useState, useCallback, useRef, useEffect } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import { supabase } from "../../../lib/supabase/supabaseClient";
import type { UIMessage } from "ai";

interface Conversation {
  id: number;
  title: string | null;
  last_message_at: string | null;
  created_at: string;
}

interface UseAiChatOptions {
  app: "dentlab" | "dentconnect";
  currentRoute: string;
}

export function useAiChat({ app, currentRoute }: UseAiChatOptions) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const conversationsLoadedRef = useRef(false);

  const chatUrl = `${import.meta.env.VITE_PDF_MICROSERVICE_URL}/api/chat`;

  const { messages, sendMessage, status, stop, setMessages } = useChat({
    transport: new DefaultChatTransport({
      api: chatUrl,
      headers: async () => {
        const { data } = await supabase.auth.getSession();
        return {
          Authorization: `Bearer ${data.session?.access_token}`,
        };
      },
      body: {
        conversationId: activeConversationId,
        app,
        currentRoute,
      },
    }),
    onFinish: () => {
      loadConversations();
    },
  });

  const loadConversations = useCallback(async () => {
    setLoadingConversations(true);
    const { data } = await supabase
      .from("ai_conversations")
      .select("id, title, last_message_at, created_at")
      .order("last_message_at", { ascending: false, nullsFirst: false })
      .limit(50);

    if (data) {
      const filtered = data.filter((c: Conversation) => {
        const meta = (c as unknown as { metadata?: { app?: string } }).metadata;
        return !meta?.app || meta.app === app;
      });
      setConversations(filtered);
    }
    setLoadingConversations(false);
  }, [app]);

  useEffect(() => {
    if (!conversationsLoadedRef.current) {
      conversationsLoadedRef.current = true;
      loadConversations();
    }
  }, [loadConversations]);

  const loadConversationMessages = useCallback(
    async (conversationId: number) => {
      setActiveConversationId(conversationId);
      const { data: rows } = await supabase
        .from("ai_conversation_messages")
        .select("data")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });

      const msgs = rows?.map((row: { data: unknown }) => row.data as UIMessage) ?? [];
      setMessages(msgs);
    },
    [setMessages]
  );

  const startNewChat = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
  }, [setMessages]);

  const sendWithPrefill = useCallback(
    (text: string) => {
      sendMessage({ parts: [{ type: "text", text }] });
    },
    [sendMessage]
  );

  return {
    messages,
    sendMessage,
    status,
    stop,
    conversations,
    activeConversationId,
    loadingConversations,
    loadConversationMessages,
    startNewChat,
    sendWithPrefill,
  };
}
